import { CSSObject } from '@emotion/react'
import { fonts, fontSizes, fontWeights } from '@opallabs/pulsar'
import { colors } from '@opallabs/spectrum'

const moduleWrapperStyle: CSSObject = {
  position: 'relative',
}

const getDragOverlay = (width: number): CSSObject => ({
  backgroundColor: 'rgba(0,0,0,0.5)',
  height: '100%',
  position: 'absolute',
  width: `${width}px`,
  zIndex: 1000,
})

const getSidebarWrapperStyle = (
  width: number,
  isHovering: boolean
): CSSObject => ({
  display: 'flex',
  left: `${width}px`,
  marginLeft: '20px',
  opacity: isHovering ? 1 : 0,
  position: 'absolute',
  top: 0,
  transition: 'opacity 0.1s ease-in-out',
  visibility: isHovering ? 'visible' : 'hidden',
})

const getGroupedSidebarButtonWrapper = (
  hasLinkPreview: boolean
): CSSObject => ({
  alignItems: 'center',
  backgroundColor: colors.n3.base,
  borderRadius: '4px',
  display: 'flex',
  height: '36px',
  justifyContent: 'space-between',
  marginRight: '10px',
  width: '72px',
  '.kuiper-modular-section-asset-picker-divider': {
    backgroundColor: colors.n1.base,
    border: `1px solid ${colors.n1.base}`,
    height: '28px',
  },
  '.kuiper-icon-wrapper': {
    color: hasLinkPreview ? colors.n5.base : colors.n6.base,
  },
})

const groupedSidebarButton: CSSObject = {
  alignItems: 'center',
  backgroundColor: colors.n3.base,
  borderRadius: '3px',
  display: 'flex',
  height: '36px',
  justifyContent: 'center',
  width: '35.5px',
}

const getDragSidebarButton = (
  width: number,
  isDragging: boolean,
  isDragZone: boolean
): CSSObject => ({
  alignItems: 'center',
  backgroundColor: colors.n3.base,
  borderRadius: isDragging && isDragZone ? '3px 3px 0 0 ' : '3px',
  color: colors.n5.base,
  cursor: isDragging && isDragZone ? 'grabbing' : 'grab',
  display: 'flex',
  fontFamily: fonts.proxima,
  fontSize: fontSizes.caps.medium,
  fontWeight: fontWeights.bold,
  height: '36px',
  justifyContent: 'center',
  left: isDragging && isDragZone ? `calc(-${width}px - 20px)` : 0,
  marginRight: '10px',
  position: isDragging && isDragZone ? 'absolute' : 'relative',
  textTransform: 'uppercase',
  transition: 'width 0.1s ease-in-out',
  width: isDragging && isDragZone ? `calc(${width}px + 138px)` : '36px',
  zIndex: 1006,
  '.kuiper-icon-wrapper': {
    color: colors.n6.base,
  },
})

const sidebarButton: CSSObject = {
  backgroundColor: colors.n3.base,
  borderRadius: '3px',
  display: 'flex',
  height: '36px',
  marginRight: '10px',
  width: '36px',
}

const getDragAreaStyles = (
  width: number,
  isDragging: boolean,
  isDragZone: boolean
): CSSObject => ({
  cursor: 'grabbing',
  height: isDragZone ? '80px' : '40px',
  position: 'absolute',
  top: isDragZone ? '-56px' : '-20px',
  width: isDragging ? `calc(${width}px + 184px)` : `${width}px`,
  zIndex: isDragging ? 1005 : -1,
})

const getFinalDragAreaStyles = (
  width: number,
  isDragging: boolean,
  isDragZone: boolean
): CSSObject => ({
  bottom: isDragZone ? '-56px' : '-20px',
  cursor: 'grabbing',
  height: isDragZone ? '80px' : '40px',
  position: 'absolute',
  width: isDragging ? `calc(${width}px + 184px)` : `${width}px`,
  zIndex: isDragging ? 1005 : -1,
})

const optionsStyling: CSSObject = {
  alignItems: 'center',
  borderRadius: '4px',
  cursor: 'pointer',
  display: 'flex',
  height: '36px',
  justifyContent: 'center',
  position: 'absolute',
  width: '36px',
  '&:hover': {
    color: 'white',
  },
  '.kuiper-icon-wrapper': {
    color: colors.n6.base,
  },
}

const getPopoverStyling = (
  tooltipActive: boolean,
  hasAssetButtons: boolean,
  isLastSection: boolean,
  height: number,
  overflowX: number
): CSSObject => ({
  background: 'white',
  borderRadius: '4px',
  boxShadow: '0 0 8px 2px rgb(0, 0, 0, 0.6)',
  display: tooltipActive ? 'inline-block' : 'none',
  left: `${(hasAssetButtons ? 20 : -62) - overflowX}px`,
  opacity: tooltipActive ? 1 : 0,
  position: 'absolute',
  top: isLastSection ? `-${height + 13}px` : '46px',
  transition: 'opacity 0.2s ease-in',
  zIndex: 10000,
  '&::after': {
    border: '10px solid white',
    borderRadius: '3px',
    content: '""',
    left: '127px',
    marginLeft: '-10px',
    position: 'absolute',
    top: isLastSection ? `${height - 13}px` : '-3px',
    transform: 'rotate(45deg)',
    zIndex: -1,
  },
})

const fileUploadStyle = {
  ...groupedSidebarButton,
  '.kuiper-modular-section-asset-picker-asset-uploader': {
    paddingTop: '6px',
  },
  input: {
    display: 'none',
  },
  label: {
    ...groupedSidebarButton,
  },
}

const styles = {
  fileUploadStyle,
  getDragAreaStyles,
  getDragOverlay,
  getDragSidebarButton,
  getFinalDragAreaStyles,
  getPopoverStyling,
  getSidebarWrapperStyle,
  groupedSidebarButton,
  getGroupedSidebarButtonWrapper,
  moduleWrapperStyle,
  optionsStyling,
  sidebarButton,
}

export { styles }
